import { 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  updateDoc,
  deleteDoc,
  query, 
  where, 
  orderBy,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

export interface Session {
  id?: string;
  userId: string;
  deviceInfo: {
    browser: string;
    os: string;
    device: string;
    userAgent: string;
  };
  location: {
    ip: string;
    city?: string;
    country?: string;
  };
  isActive: boolean;
  lastActivity: Timestamp;
  createdAt: Timestamp;
  sessionToken: string;
}

export interface DeviceInfo {
  browser: string;
  os: string;
  device: string;
  userAgent: string;
}

export interface LocationInfo {
  ip: string;
  city?: string;
  country?: string;
}

class SessionService {
  private collectionName = 'userSessions';

  /**
   * Create a new session for a user
   */
  async createSession(
    userId: string, 
    deviceInfo: DeviceInfo, 
    locationInfo: LocationInfo,
    sessionToken: string
  ): Promise<string> {
    try {
      const now = Timestamp.now();
      const sessionData: Omit<Session, 'id'> = {
        userId,
        deviceInfo,
        location: locationInfo,
        isActive: true,
        lastActivity: now,
        createdAt: now,
        sessionToken
      };

      const docRef = await addDoc(collection(db, this.collectionName), sessionData);
      console.log('✅ Session created successfully:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('❌ Error creating session:', error);
      throw error;
    }
  }

  /**
   * Get all active sessions for a user
   */
  async getUserSessions(userId: string): Promise<Session[]> {
    try {
      console.log('🔍 Querying sessions for user:', userId);
      
      // Use simple query to avoid index issues
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      const sessions: Session[] = [];
      
      console.log('📊 Found', querySnapshot.size, 'sessions in database');
      
      querySnapshot.forEach((doc) => {
        const sessionData = { id: doc.id, ...doc.data() } as Session;
        console.log('📝 Session data:', sessionData);
        sessions.push(sessionData);
      });
      
      // Sort sessions by lastActivity
      sessions.sort((a, b) => {
        const aTime = a.lastActivity?.toDate ? a.lastActivity.toDate().getTime() : 0;
        const bTime = b.lastActivity?.toDate ? b.lastActivity.toDate().getTime() : 0;
        return bTime - aTime;
      });
      
      return sessions;
    } catch (error) {
      console.error('❌ Error getting user sessions:', error);
      // Return empty array instead of throwing error
      return [];
    }
  }

  /**
   * Update session activity
   */
  async updateSessionActivity(sessionId: string): Promise<void> {
    try {
      const sessionRef = doc(db, this.collectionName, sessionId);
      await updateDoc(sessionRef, {
        lastActivity: serverTimestamp()
      });
    } catch (error) {
      console.error('❌ Error updating session activity:', error);
      throw error;
    }
  }

  /**
   * Deactivate a session (logout)
   */
  async deactivateSession(sessionId: string): Promise<void> {
    try {
      const sessionRef = doc(db, this.collectionName, sessionId);
      await updateDoc(sessionRef, {
        isActive: false,
        lastActivity: serverTimestamp()
      });
      console.log('✅ Session deactivated successfully');
    } catch (error) {
      console.error('❌ Error deactivating session:', error);
      throw error;
    }
  }

  /**
   * Delete a session completely
   */
  async deleteSession(sessionId: string): Promise<void> {
    try {
      const sessionRef = doc(db, this.collectionName, sessionId);
      await deleteDoc(sessionRef);
      console.log('✅ Session deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting session:', error);
      throw error;
    }
  }

  /**
   * Deactivate all sessions for a user except the current one
   */
  async deactivateAllOtherSessions(userId: string, currentSessionId: string): Promise<void> {
    try {
      const sessions = await this.getUserSessions(userId);
      const otherSessions = sessions.filter(session => 
        session.id !== currentSessionId && session.isActive
      );

      const promises = otherSessions.map(session => 
        this.deactivateSession(session.id!)
      );

      await Promise.all(promises);
      console.log('✅ All other sessions deactivated');
    } catch (error) {
      console.error('❌ Error deactivating other sessions:', error);
      throw error;
    }
  }

  /**
   * Clean up old inactive sessions (older than 30 days)
   */
  async cleanupOldSessions(): Promise<void> {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const thirtyDaysAgoTimestamp = Timestamp.fromDate(thirtyDaysAgo);

      const q = query(
        collection(db, this.collectionName),
        where('isActive', '==', false),
        where('lastActivity', '<', thirtyDaysAgoTimestamp)
      );

      const querySnapshot = await getDocs(q);
      const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
      
      await Promise.all(deletePromises);
      console.log('✅ Old sessions cleaned up');
    } catch (error) {
      console.error('❌ Error cleaning up old sessions:', error);
      throw error;
    }
  }

  /**
   * Get current session ID from localStorage
   */
  getCurrentSessionId(): string | null {
    return localStorage.getItem('scubamail_session_id');
  }

  /**
   * Set current session ID in localStorage
   */
  setCurrentSessionId(sessionId: string): void {
    localStorage.setItem('scubamail_session_id', sessionId);
  }

  /**
   * Clear current session ID from localStorage
   */
  clearCurrentSessionId(): void {
    localStorage.removeItem('scubamail_session_id');
  }

  /**
   * Generate a unique session token
   */
  generateSessionToken(): string {
    return 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  }
}

export const sessionService = new SessionService();
